import React, { forwardRef, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Row from '@polestar/component-warehouse-react/components/row/Row';
import Alignments, { MainAxisAlignments, } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import IconButton, { IconButtonVariants, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import Column from '@polestar/component-warehouse-react/components/column/Column';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Label from '@polestar/component-warehouse-react/components/label/Label';
import Backdrop from '@polestar/component-warehouse-react/components/backdrop/Backdrop';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import { useModal } from '@polestar/component-warehouse-react/contexts/modal';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
const SelectionOverlay = forwardRef(({ children, label, onBack, input, ...props }, ref) => {
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(internalRef, ref);
    useModal({
        ref: internalRef,
        open: true,
        applyInputFix: !!input,
        closeHandler: useCallback(() => {
            // we don't need a close handler because selection overlay only renders when opened
        }, []),
    });
    return (React.createElement(Portal, null,
        React.createElement(Backdrop, { visible: true }),
        React.createElement(Container, { ...props, ref: combinedRef, "data-type": "selection-overlay-wrapper" },
            React.createElement(HeaderBlock, null,
                React.createElement(Row, { mainAxisAlignment: MainAxisAlignments.spaceBetween },
                    React.createElement(BackButton, { icon: Icons.arrowLeft, onClick: onBack, variant: IconButtonVariants.transparent, size: Sizes.large, alignmentOffset: Alignments.left })),
                label && React.createElement(StyledLabel, null, label),
                input && React.createElement(InputContainer, null, input)),
            React.createElement(ContentBlock, { "data-type": "selection-overlay" }, children))));
});
SelectionOverlay.displayName = 'SelectionOverlay';
export default SelectionOverlay;
const Container = styled.div(({ theme }) => css `
    background-color: ${theme.selectionOverlay.backgroundColor};
    overflow: hidden;
    position: fixed;
    display: flex;
    flex-direction: column;
    inset-block-start: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 100%;
    overscroll-behavior: contain;
    z-index: ${LayoutIndexes.modal};
  `);
Container.displayName = `Container`;
const BackButton = styled(IconButton)(({ theme }) => css `
    color: ${theme.selectionOverlay.backButtonColor};
  `);
const HeaderBlock = styled(Column) `
  width: 100%;
  padding: ${Spacings.small} ${Spacings.large} 0 ${Spacings.large};
`;
const ContentBlock = styled(Column) `
  flex: 1;
  overflow-y: auto;
`;
const StyledLabel = styled(Label)(({ theme }) => css `
    padding: ${Spacings.xLarge} 0 ${Spacings.xSmall} 0;
    border-block-end: 1px solid ${theme.selectionOverlay.labelBorderColor};
  `);
const InputContainer = styled(Label) `
  padding-block-start: ${createCustomSpacing(20 / 16)};
  padding-block-end: ${Spacings.xSmall};
`;
